import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, AlertOctagon, ArrowUpRight, Car, CircleOff, PauseCircle, Radar, Wifi } from "lucide-react";
import { PageHeader, StatCard, StatusBadge, Empty } from "@/components/common";
import { FleetMap } from "@/components/map/FleetMap";
import { EventItem } from "@/components/EventItem";
import { QuickActions } from "@/components/QuickActions";
import { EngineCommand } from "@/components/EngineCommand";
import { SpeedGauge } from "@/components/VehicleInfoPanel";
import { VehicleTypeIcon } from "@/components/VehicleRow";
import { useFleet } from "@/context/FleetContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { timeAgo, vehicleStatus, isGpsFresh } from "@/lib/fleet";

function SelectedCard({ v }) {
  if (!v) return <div className="gt-card"><Empty title="Pilih kendaraan" text="Klik marker di peta untuk melihat informasi." /></div>;
  const st = vehicleStatus(v);
  return (
    <div className="gt-card p-4" data-testid="dashboard-selected-vehicle">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent"><VehicleTypeIcon type={v.type} className="h-5 w-5" /></div>
          <div className="min-w-0">
            <div className="truncate font-bold">{v.name}</div>
            <div className="font-mono text-xs text-muted-foreground">{v.plate} · {timeAgo(v.server_time)}</div>
          </div>
        </div>
        <StatusBadge status={st} />
      </div>
      <div className="my-3 flex items-end justify-between">
        <SpeedGauge speed={isGpsFresh(v) ? (v.speed || 0) : 0} limit={v.speed_limit} status={st} />
        <div className="space-y-1 text-right text-xs">
          <div className="gt-eyebrow">ACC</div>
          <div className={`font-mono font-bold ${v.acc ? "text-emerald-500" : "text-slate-400"}`}>{v.acc === true ? "ON" : v.acc === false ? "OFF" : "BELUM ADA DATA"}</div>
          <div className="gt-eyebrow pt-1">Posisi</div>
          <div className="font-mono">{isGpsFresh(v) ? `${v.lat?.toFixed(4)}, ${v.lng?.toFixed(4)}` : "—"}</div>
        </div>
      </div>
      <QuickActions vehicleId={v.id} />
    </div>
  );
}

export default function Dashboard() {
  const { vehicles, byId, liveEvents } = useFleet();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  const stats = useMemo(() => {
    const c = { moving: 0, idle: 0, stopped: 0, offline: 0 };
    vehicles.forEach((v) => (c[vehicleStatus(v)] += 1));
    return { total: vehicles.length, online: vehicles.length - c.offline, offline: c.offline, moving: c.moving, stopped: c.idle + c.stopped };
  }, [vehicles]);

  const events = useMemo(() => {
    const seen = new Set();
    return [...liveEvents, ...(data?.recent_events || [])].filter((e) => !seen.has(e.id) && seen.add(e.id)).slice(0, 8);
  }, [liveEvents, data]);

  const selected = (sel && byId[sel]) || vehicles.find((v) => vehicleStatus(v) === "moving") || vehicles[0];
  const rows = vehicles.filter((v) => {
    const s = vehicleStatus(v);
    return filter === "all" || (filter === "online" ? s !== "offline" : filter === "stopped" ? ["idle", "stopped"].includes(s) : s === filter);
  });

  const cards = [
    ["all", "Total Kendaraan", stats.total, Car, "yellow"],
    ["online", "Online", stats.online, Wifi, "green"],
    ["offline", "Offline", stats.offline, CircleOff, "slate"],
    ["moving", "Bergerak", stats.moving, Activity, "green"],
    ["stopped", "Berhenti", stats.stopped, PauseCircle, "blue"],
    ["alarm", "Alarm 24 jam", data?.stats?.alarm ?? "–", AlertOctagon, "red"],
  ];

  return (
    <div>
      <PageHeader
        eyebrow={new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        title={`Halo, ${user?.name?.split(" ")[0]}`}
        subtitle={user?.role === "admin" ? "Ringkasan seluruh armada di platform." : `Armada ${user?.customer?.name || "Anda"} hari ini.`}
        actions={<Link to="/live" className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90" data-testid="dashboard-open-live"><Radar className="h-4 w-4" /> Live Tracking</Link>}
      />
      <div className="gt-noscroll -mx-4 mb-6 flex gap-3 overflow-x-auto px-4 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 xl:grid-cols-6" data-testid="dashboard-stats">
        {cards.map(([k, label, val, icon, tone], i) => (
          <StatCard key={k} label={label} value={val} icon={icon} tone={tone} delay={i * 50} testId={`stat-${k}`} active={filter === k}
            onClick={() => (k === "alarm" ? nav("/alarms") : setFilter(k))} />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <div className="gt-card overflow-hidden lg:col-span-8 gt-rise">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div><div className="text-sm font-bold">Peta Realtime</div><div className="text-xs text-muted-foreground">{stats.online} kendaraan online</div></div>
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-500"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 gt-blink" /> LIVE</span>
          </div>
          <FleetMap vehicles={vehicles} selectedId={selected?.id} onSelect={setSel} className="h-[320px] sm:h-[460px]" testId="dashboard-map" />
        </div>
        <div className="space-y-5 lg:col-span-4">
          <SelectedCard v={selected} />
          {selected && <EngineCommand vehicle={selected} />}
          <div className="gt-card" data-testid="dashboard-events">
            <div className="flex items-center justify-between px-4 pt-4">
              <div className="text-sm font-bold">Event Terbaru</div>
              <Link to="/alarms" className="text-xs font-semibold gt-brand-text">Lihat semua</Link>
            </div>
            <div className="gt-scroll max-h-[300px] overflow-y-auto p-1.5">
              {events.map((e) => <EventItem key={e.id} e={e} compact />)}
              {!events.length && <Empty title="Belum ada event" />}
            </div>
          </div>
        </div>
      </div>
      <div className="gt-card mt-5 overflow-hidden gt-rise" data-testid="dashboard-vehicle-table">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="text-sm font-bold">Daftar Kendaraan <span className="ml-1 font-mono text-xs text-muted-foreground">{rows.length}</span></div>
          <Link to="/vehicles" className="text-xs font-semibold gt-brand-text">Kelola</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-y border-border bg-background/40 text-left">
              <tr className="[&>th]:gt-eyebrow [&>th]:px-4 [&>th]:py-2.5">
                <th>Kendaraan</th><th>Status</th><th>Kecepatan</th><th>ACC</th><th className="hidden md:table-cell">Koordinat</th><th className="hidden sm:table-cell">Update</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} onClick={() => nav(`/vehicles/${v.id}`)} className="cursor-pointer border-b border-border/60 transition-colors hover:bg-accent/50" data-testid={`dashboard-row-${v.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold">{v.name}</div><div className="font-mono text-xs text-muted-foreground">{v.plate}</div></td>
                  <td className="px-4"><StatusBadge status={vehicleStatus(v)} /></td>
                  <td className="px-4 font-mono">{isGpsFresh(v) ? `${Math.round(v.speed || 0)} km/j` : "—"}</td>
                  <td className={`px-4 font-mono text-xs font-bold ${v.acc ? "text-emerald-500" : "text-slate-400"}`}>{v.acc === true ? "ON" : v.acc === false ? "OFF" : "BELUM ADA DATA"}</td>
                  <td className="hidden px-4 font-mono text-xs text-muted-foreground md:table-cell">{isGpsFresh(v) ? `${v.lat?.toFixed(5)}, ${v.lng?.toFixed(5)}` : "—"}</td>
                  <td className="hidden px-4 text-xs text-muted-foreground sm:table-cell">{timeAgo(v.server_time)}</td>
                  <td className="px-4"><ArrowUpRight className="h-4 w-4 text-muted-foreground" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
